import type { LatLngTuple } from "@fortex/api";
import type { DrawMode } from "../core/options";
import type { Labels } from "../i18n/labels";

export type ValidationResult = {
  valid: boolean;
  reason: string | null;
};

function orientation(a: LatLngTuple, b: LatLngTuple, c: LatLngTuple): number {
  const value = (b[1] - a[1]) * (c[0] - a[0]) - (b[0] - a[0]) * (c[1] - a[1]);

  if (Math.abs(value) < 1e-14) return 0;

  return value > 0 ? 1 : -1;
}

function onSegment(a: LatLngTuple, b: LatLngTuple, c: LatLngTuple): boolean {
  return Math.min(a[0], b[0]) <= c[0] && c[0] <= Math.max(a[0], b[0]) && Math.min(a[1], b[1]) <= c[1] && c[1] <= Math.max(a[1], b[1]);
}

export function segmentsIntersect(a: LatLngTuple, b: LatLngTuple, c: LatLngTuple, d: LatLngTuple): boolean {
  const o1 = orientation(a, b, c);
  const o2 = orientation(a, b, d);
  const o3 = orientation(c, d, a);
  const o4 = orientation(c, d, b);

  if (o1 !== o2 && o3 !== o4) return true;
  if (o1 === 0 && onSegment(a, b, c)) return true;
  if (o2 === 0 && onSegment(a, b, d)) return true;
  if (o3 === 0 && onSegment(c, d, a)) return true;
  if (o4 === 0 && onSegment(c, d, b)) return true;

  return false;
}

export function ringSelfIntersects(points: LatLngTuple[]): boolean {
  const count = points.length;

  if (count < 4) return false;

  for (let first = 0; first < count; first++) {
    const a = points[first];
    const b = points[(first + 1) % count];

    for (let second = first + 2; second < count; second++) {
      if (first === 0 && second === count - 1) continue;

      const c = points[second];
      const d = points[(second + 1) % count];

      if (segmentsIntersect(a, b, c, d)) return true;
    }
  }

  return false;
}

export function validate(mode: DrawMode | null, points: LatLngTuple[], labels: Labels): ValidationResult {
  switch (mode) {
    case "point":
      return { valid: points.length >= 1, reason: null };
    case "polyline":
      return points.length >= 2 ? { valid: true, reason: null } : { valid: false, reason: labels.drawNeedsTwo };
    case "polygon":
      if (points.length < 3) return { valid: false, reason: labels.drawNeedsThree };
      if (ringSelfIntersects(points)) return { valid: false, reason: labels.drawSelfIntersects };

      return { valid: true, reason: null };
    default:
      return { valid: false, reason: null };
  }
}
